// noinspection JSUnusedGlobalSymbols

import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { SubscribedContainer } from 'app/shared/util/subscribed-container';
import { HttpErrorResponse, HttpHeaders, HttpResponse } from '@angular/common/http';
import { NgxSpinnerService } from 'ngx-spinner';
import { AuthorityEnum } from '../../../../../models/enums/authority.enum';
import { ASC, DESC, ITEMS_PER_PAGE } from '../../../../../models/constants/pagination.constants';
import { Pagination } from '../../../../../models/pagination-table.model';
import { ActivatedRoute, Router } from '@angular/router';
import { IBaryTableFetchService, TableOptions } from '../../../../../services/i-bary-table-fetch-service';
import { BaryTableDataEnum } from '../../../../../models/enums/bary-table.enum';
import { AlertService } from '../../../../../services/alert.service';
import { take, takeUntil, throttleTime } from 'rxjs/operators';
import { BaryFilterFactoryService } from '../../../../../services/bary-filter-factory.service';
import { ParseLinks } from '../../../../../services/parse-links.service';
import { Subject } from 'rxjs';
import { AttributeFilter } from '../../models/attribute-filter';
import { ICustomer } from '../../../../../models/customer.model';
import { AccountService } from '../../../../../services/account.service';
import { Transporter } from '../../../../../models/transporter.model';
import { Offer } from '../../../../../models/offer.model';
import { CurrentDataService } from '../../../../../services/current-data-service';

@Component({
  selector: 'jhi-bary-table-manager',
  templateUrl: './bary-table-manager.component.html',
  styleUrls: ['./bary-table-manager.component.scss'],
})
export class BaryTableManagerComponent<T> extends SubscribedContainer implements OnInit {
  readonly #spinnerService = inject(NgxSpinnerService);
  readonly #parseLinks = inject(ParseLinks);
  readonly #jhiAlertService = inject(AlertService);
  readonly #activatedRoute = inject(ActivatedRoute);
  readonly #baryFilterFactoryService = inject(BaryFilterFactoryService);
  readonly #currentDataService = inject(CurrentDataService);
  readonly #accountService = inject(AccountService);
  readonly #router = inject(Router);

  @Input() type!: BaryTableDataEnum;
  @Input() service!: IBaryTableFetchService<T>;
  @Input() tableOptions: TableOptions = {};
  @Input() forceRefreshEvent?: EventEmitter<any>;

  @Output() transitionEvent = new EventEmitter();
  @Output() loadedEvent = new EventEmitter();

  public readonly BaryTableDataEnum = BaryTableDataEnum;

  public data: any;
  public routeData: any;
  public filters: any;
  public links: any;

  public ITEMS_PER_PAGE = ITEMS_PER_PAGE;

  public totalItems = 0;
  public loaded = false;
  public activeFilter = false;
  public loading = false;
  public spinnerLoading = 'loadingTableData';
  public subject = new Subject();

  public pagination: Pagination = new Pagination(1, 1, '', '');

  public currentTransporter?: Transporter;
  public currentOffer?: Offer;
  public filtersOptions?: AttributeFilter[];
  public forCustomer?: ICustomer;
  public currentCustomer?: ICustomer;

  constructor() {
    super();
  }

  private initFiltersOption(): void {
    switch (this.type) {
      case BaryTableDataEnum.TRANSPORT_ORDERS:
        this.filtersOptions = this.#baryFilterFactoryService.getTransportsFilters();
        break;
      case BaryTableDataEnum.CUSTOMER_BILLS:
        this.filtersOptions = this.#baryFilterFactoryService.getBillsForCustomerFilters();
        break;
      case BaryTableDataEnum.MODERATOR_BILLS:
        this.filtersOptions = this.#baryFilterFactoryService.getBillsForModeratorFilters();
        break;
      case BaryTableDataEnum.TRANSPORTER_MEMBERS:
        this.filtersOptions = this.#baryFilterFactoryService.getTransporterMembersFilters();
        break;
      case BaryTableDataEnum.SERVICES:
        this.filtersOptions = this.#baryFilterFactoryService.getServicesFilters();
        break;
      case BaryTableDataEnum.NOTIFICATIONS:
        this.filtersOptions = this.#baryFilterFactoryService.getNotificationsFilters();
        break;
      case BaryTableDataEnum.CUSTOMERS:
        this.filtersOptions = this.#baryFilterFactoryService.getCustomersFilters();
        break;
      case BaryTableDataEnum.UPLOAD_PRICING_ITEMS:
        this.filtersOptions = this.#baryFilterFactoryService.getPricingUploadFilters();
        break;
      case BaryTableDataEnum.CUSTOMER_PRICING:
        this.filtersOptions = this.#baryFilterFactoryService.getPricingUploadFilters();
        break;
      case BaryTableDataEnum.ACCEPTANCES:
        this.filtersOptions = this.#baryFilterFactoryService.getAcceptanceFilters();
        break;
      case BaryTableDataEnum.QUOTES:
        this.filtersOptions = this.#baryFilterFactoryService.getQuotationFilters();
        break;
      case BaryTableDataEnum.OFFERS:
        // TODO: will be rework soon
        // this.filtersOptions = this.#baryFilterFactoryService.getOfferFilters();
        break;
      case BaryTableDataEnum.INSURANCE_ITEMS:
        this.filtersOptions = this.#baryFilterFactoryService.getOfferInsuranceItemsFilters();
        break;
      case BaryTableDataEnum.USERS:
        this.filtersOptions = this.#baryFilterFactoryService.getUsersFilters();
        break;
      case BaryTableDataEnum.IMPERSONATE:
        this.filtersOptions = this.#baryFilterFactoryService.getUsersFilters();
        break;
      case BaryTableDataEnum.REFUNDS:
        this.filtersOptions = this.#baryFilterFactoryService.getRefundsFilters();
        break;
      case BaryTableDataEnum.SPECIAL_PLACE:
        this.filtersOptions = this.#baryFilterFactoryService.getSpecialDestinationFilters();
        break;
      case BaryTableDataEnum.ADDRESSES:
        this.filtersOptions = this.#baryFilterFactoryService.getAddressFilters();
        break;
      case BaryTableDataEnum.TRANSPORTS_PENDING:
        this.filtersOptions = this.#baryFilterFactoryService.getUnvalidatedBasketsFilters();
        break;
    }
  }

  protected sort(): string[] {
    const result = [this.pagination.predicate + ',' + (this.pagination.reverse ? ASC : DESC)];
    if (this.pagination.predicate !== 'id') {
      result.push('id');
    }
    return result;
  }

  public ngOnInit(): void {
    this.routeData = this.#activatedRoute.data.pipe(take(1)).subscribe(data => {
      this.pagination.page = data['pagingParams']!.page;
      this.pagination.previousPage = data['pagingParams']!.page;
      this.pagination.reverse = data['pagingParams']!.ascending;
      this.pagination.predicate = data['pagingParams']!.predicate;
      this.currentTransporter = data['pagingParams']!.transporter;
      this.ITEMS_PER_PAGE = this.type === BaryTableDataEnum.OFFERS ? 100 : this.ITEMS_PER_PAGE;
      this.filters = {
        page: this.pagination.page,
        size: this.ITEMS_PER_PAGE,
        sort: this.sort(),
      };
    });

    this.initFiltersOption();
    this.checkScope();
    this.subject.pipe(takeUntil(this.destroyed$), throttleTime(300)).subscribe(() => {
      this.#spinnerService.show().then();
      const filters = this.prepareCriteria();
      this.service.query(filters, this.tableOptions).subscribe(
        (res: HttpResponse<T[]>) => this.paginateCurrent(res.body, res.headers),
        (res: HttpErrorResponse) => this.onError(res.message)
      );
    });
    this.loadAll();

    this.forceRefreshEvent?.subscribe(() => {
      this.loadAll();
    });
  }

  public loadAll(): void {
    this.subject.next();
  }

  private prepareCriteria(): any {
    const filters = this.filters;

    if (filters.page) {
      filters.page = filters.page - 1;
    }

    if (this.type !== BaryTableDataEnum.USERS) {
      if (this.currentTransporter) {
        filters['transporterId.equals'] = this.currentTransporter.id;
      }
      if (this.forCustomer) {
        filters['customerId.equals'] = this.forCustomer.id;
      } else if (!this.tableOptions.isModerator) {
        filters['customerId.equals'] = this.currentCustomer?.id;
      }
    }

    if (this.type === BaryTableDataEnum.CUSTOMERS && this.tableOptions.isActivatedCustomers != null) {
      filters['userActivated.equals'] = this.tableOptions.isActivatedCustomers;
    }

    this.filters.sort = this.sort();
    return filters;
  }

  protected loadPage(page: number): void {
    if (page !== this.pagination.previousPage) {
      this.pagination.previousPage = page;
      this.transition();
    }
  }

  public transition(): void {
    this.loading = true;
    this.filters['page'] = this.pagination.page;
    this.filters['size'] = this.ITEMS_PER_PAGE;
    this.filters['sort'] = this.sort();

    if (isNaN(this.filters['page'])) {
      this.filters['page'] = 1;
    }

    const queryObject = this.getQueryObject();

    this.#router
      .navigate([location.pathname], {
        queryParams: queryObject,
      })
      .then();

    this.loadAll();

    this.transitionEvent.emit();
  }

  private paginateCurrent(data: any[] | null, headers: HttpHeaders): void {
    const headerLink = headers.get('link') ? headers.get('link') : null;
    if (headerLink != null) {
      this.links = this.#parseLinks.parse(headerLink);
    }

    const totalCounts = headers.get('X-Total-Count') ? headers.get('X-Total-Count') : null;
    if (totalCounts != null) {
      this.totalItems = parseInt(totalCounts, 10);
    }

    this.activeFilter = Object.keys(this.filters).length > 3;

    if (data && data.length > 0) {
      this.service
        .fillDataToShow(data, this.tableOptions)
        .pipe(take(1))
        .subscribe(filledData => {
          this.data = filledData;
          this.#spinnerService.hide().then();
          this.loaded = true;
          this.loadedEvent.emit(true);
          this.loading = false;
        });
    } else {
      this.data = [];
      this.#spinnerService.hide().then();
      this.loaded = true;
      this.loadedEvent.emit(true);
      this.loading = false;
    }
  }

  private getQueryObject(): { size: any; page: any; sort: string } {
    const queryObj = Object.create({});
    queryObj['page'] = this.filters['page'];
    queryObj['size'] = this.filters['size'];
    queryObj['sort'] = this.pagination.predicate + ',' + (this.pagination.reverse ? 'asc' : 'desc');
    return queryObj as { size: any; page: any; sort: string };
  }

  protected filtersCurrentChanged(updatedFilters: any): void {
    this.filters = updatedFilters;
    this.loadAll();
  }

  private onError(error: string): void {
    this.#jhiAlertService.addAlert({ type: 'danger', message: error });
    this.#spinnerService.hide().then();
    this.loaded = true;
    this.loadedEvent.emit(true);
    this.loading = false;
  }

  public getData(): T[] | any | undefined {
    return this.data;
  }

  private checkScope(): void {
    this.#currentDataService
      .getCustomer()
      .pipe(take(1))
      .subscribe(customer => {
        this.currentCustomer = customer;
      });

    if (this.#accountService.hasAnyAuthority(AuthorityEnum.MODERATOR)) {
      this.tableOptions.isModerator = true;
    }

    if (this.#router.url.includes('/middle/customer/')) {
      this.#activatedRoute.parent?.data.pipe(take(1)).subscribe(data => {
        if (data['customer']) {
          this.forCustomer = data['customer'];
          this.tableOptions.focusForCustomer = true;
        }
      });
    }
  }

  protected createNew($event: any): void {
    if (this.service.create) {
      this.service.create($event, this.tableOptions).subscribe(() => {
        this.transition();
      });
    } else {
      console.warn('create called, but not implemented in service.');
    }
  }

  protected createAndRedirect($event: any): void {
    if (this.service.create) {
      this.service.create($event, this.tableOptions).subscribe(entity => {
        const body: any = entity.body;
        if (body) {
          this.#router.navigate([body['id']]).then();
        }
      });
    } else {
      console.warn('create called, but not implemented in service.');
    }
  }
}
